import React, { useState, useEffect } from 'react';
import { Plus, Users, Search } from 'lucide-react';
import api from '../utils/api'; // Adjust path if needed (e.g., ../utils/api)
import TeamHierarchy from './TeamHierarchy';
import CreateTeamModal from './CreateTeamModal';
import TeamDetailsPanel from './TeamDetailsPanel';
import AddTeamMemberModal from './AddTeamMemberModal';
import { toast } from 'react-hot-toast';

const TeamsTab = () => {
    const [teams, setTeams] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [editingTeam, setEditingTeam] = useState(null);
    const [parentTeamForSubteam, setParentTeamForSubteam] = useState(null);

    // Selected Team State
    const [selectedTeamId, setSelectedTeamId] = useState(null);
    const [selectedTeamDetails, setSelectedTeamDetails] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [teamActivity, setTeamActivity] = useState([]);
    const [detailsLoading, setDetailsLoading] = useState(false);

    // Add Member Modal State
    const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);

    // Fetch teams
    const fetchTeams = async () => {
        setLoading(true);
        try {
            const response = await api.get('/teams');
            setTeams(response.data.teams || []);
        } catch (error) {
            console.error('Error fetching teams:', error);
            toast.error('Failed to load teams');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeams();
    }, []);

    // Fetch team details when selected
    const fetchTeamDetails = async (teamId) => {
        setDetailsLoading(true);
        try {
            const [detailsRes, activityRes] = await Promise.allSettled([
                api.get(`/teams/${teamId}`),
                api.get(`/teams/${teamId}/activity`)
            ]);

            if (detailsRes.status === 'fulfilled') {
                setSelectedTeamDetails(detailsRes.value.data.team);
                setTeamMembers(detailsRes.value.data.members || []);
            } else {
                throw detailsRes.reason;
            }

            if (activityRes.status === 'fulfilled') {
                setTeamActivity(activityRes.value.data.activity || []);
            } else {
                // If activity endpoint fails, set empty array but don't crash
                console.warn('Failed to fetch team activity', activityRes.reason);
                setTeamActivity([]);
            }

        } catch (error) {
            console.error('Error fetching team details:', error);
            toast.error('Failed to load team details');
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleTeamSelect = (team) => {
        setSelectedTeamId(team.id);
        fetchTeamDetails(team.id);
    };

    const handleCreateSuccess = () => {
        fetchTeams();
        toast.success(editingTeam ? 'Team updated successfully' : 'Team created successfully');
        setEditingTeam(null);
        setParentTeamForSubteam(null);

        // Refresh selected team details if relevant
        if (selectedTeamId) {
            fetchTeamDetails(selectedTeamId);
        }
    };

    const handleDeleteTeam = async (team) => {
        if (!window.confirm(`Are you sure you want to delete ${team.team_name}? Sub-teams may be orphaned.`)) return;

        try {
            await api.delete(`/teams/${team.id}`);
            toast.success('Team deleted successfully');
            fetchTeams();
            if (selectedTeamId === team.id) {
                setSelectedTeamId(null);
                setSelectedTeamDetails(null);
                setTeamMembers([]);
            }
        } catch (error) {
            console.error('Error deleting team:', error);
            toast.error('Failed to delete team');
        }
    };

    const handleRemoveMember = async (teamId, employeeId) => {
        try {
            await api.delete(`/teams/${teamId}/employees/${employeeId}`);
            toast.success('Member removed successfully');
            fetchTeamDetails(teamId); // Refresh details
            fetchTeams(); // Refresh counts in sidebar
        } catch (error) {
            console.error('Error removing member:', error);
            toast.error('Failed to remove member');
        }
    };

    const handleAddMember = (team) => {
        setIsAddMemberModalOpen(true);
    };

    const handleMemberAdded = () => {
        if (selectedTeamId) {
            fetchTeamDetails(selectedTeamId);
            fetchTeams(); // To update member counts in tree
        }
    };

    const openEditModal = (team) => {
        setEditingTeam(team);
        setIsCreateModalOpen(true);
    };

    const openAddSubteamModal = (parentTeam) => {
        setParentTeamForSubteam(parentTeam);
        setEditingTeam(null); // Ensure we are creating new
        setIsCreateModalOpen(true);
    };

    const openCreateModal = () => {
        setEditingTeam(null);
        setParentTeamForSubteam(null);
        setIsCreateModalOpen(true);
    };

    return (
        <div className="space-y-6">
            {/* Header Actions */}
            <div className="md:hidden flex justify-end">
                <button
                    onClick={openCreateModal}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-5 py-2.5 rounded-xl font-medium transition-all shadow-lg shadow-gray-900/20"
                >
                    <Plus size={20} />
                    Create Team
                </button>
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex justify-center p-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                    {/* Left: Hierarchy Tree */}
                    <div>
                        <TeamHierarchy
                            teams={teams}
                            onEdit={openEditModal}
                            onDelete={handleDeleteTeam}
                            onAddSubteam={openAddSubteamModal}
                            onSelect={handleTeamSelect}
                            selectedTeamId={selectedTeamId}
                        />
                    </div>

                    {/* Right: Members / Details Preview */}
                    <div>
                        <TeamDetailsPanel
                            team={selectedTeamDetails}
                            members={teamMembers}
                            activity={teamActivity}
                            loading={detailsLoading}
                            onAddMember={handleAddMember}
                            onRemoveMember={handleRemoveMember}
                            onEditTeam={openEditModal}
                        />
                    </div>
                </div>
            )}

            {/* Modals */}
            <CreateTeamModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSuccess={handleCreateSuccess}
                initialData={editingTeam} // Need to implement this in CreateTeamModal if editing supported
                initialParentId={parentTeamForSubteam?.id} // Need to implement this
            />

            <AddTeamMemberModal
                isOpen={isAddMemberModalOpen}
                onClose={() => setIsAddMemberModalOpen(false)}
                teamId={selectedTeamId}
                teamName={selectedTeamDetails?.team_name}
                onMemberAdded={handleMemberAdded}
                existingMemberIds={teamMembers.map(m => m.id)}
            />
        </div>
    );
};

export default TeamsTab;
